import { useEffect, useCallback, useRef } from 'react';
import { useAppDispatch, useAppSelector } from './useAppHooks';
import {
  setConnected, setRobotsOnline, setTelemetry, setCommandAck,
  addSubscribedRobot, removeSubscribedRobot, addRobotOnline, removeRobotOnline,
} from '../features/websocket/websocketSlice';
import {
  initializeSocket, disconnectSocket, addEventListener, removeEventListener,
  subscribeToRobot as wsSubscribe, unsubscribeFromRobot as wsUnsubscribe,
  sendTeleop as wsTeleop, sendNavGoal as wsNavGoal, cancelNavigation as wsCancel,
  emergencyStop as wsEmergencyStop, isSocketConnected, getSocket,
  notifyUserLoggedIn,
} from '../utils/socketClient';

// Global flags to prevent multiple initializations/notifications
let globalSocketInitialized = false;
let globalLoginNotified = false;  // Module-level: shared across ALL hook instances

export const useWebSocket = () => {
  const dispatch = useAppDispatch();
  const { isAuthenticated, user } = useAppSelector((state) => state.auth);
  const { robots } = useAppSelector((state) => state.robots);
  const { isConnected, robotsOnline, telemetry, subscribedRobots } = useAppSelector((state) => state.websocket);
  const initRef = useRef(false);

  useEffect(() => {
    if (!isAuthenticated) return;
    
    // Initialize socket once globally
    let socket = getSocket();
    if (!globalSocketInitialized || !socket) {
      if (initRef.current) return;
      initRef.current = true;
      globalSocketInitialized = true;
      socket = initializeSocket();
    }
    
    if (!socket) return;
    
    // Always dispatch connected state
    if (isSocketConnected()) {
      dispatch(setConnected(true));
    }

    const handleConnect = () => {
      console.log('WebSocket connected');
      dispatch(setConnected(true));
      globalLoginNotified = false;
    };
    
    const handleDisconnect = () => {
      console.log('WebSocket disconnected');
      dispatch(setConnected(false));
    };
    
    const handleRobotsOnline = (data: { robots: string[] }) => {
      console.log('Robots online update:', data.robots);
      dispatch(setRobotsOnline(data.robots));
    };
    
    const handleTelemetry = (data: any) => {
      dispatch(setTelemetry(data));
    };
    
    const handleCommandAck = (data: any) => dispatch(setCommandAck(data));
    
    const handleRobotStatus = (data: { robot_serial: string; status: string }) => {
      console.log('Robot status update:', data.robot_serial, data.status);
      if (data.status === 'online') {
        dispatch(addRobotOnline(data.robot_serial));
      } else {
        dispatch(removeRobotOnline(data.robot_serial));
      }
    };

    socket.on('connect', handleConnect);
    socket.on('disconnect', handleDisconnect);
    addEventListener('robots_online', handleRobotsOnline);
    addEventListener('telemetry_update', handleTelemetry);
    addEventListener('command_ack', handleCommandAck);
    addEventListener('robot_status', handleRobotStatus);

    // If socket is already connected, trigger the connected state
    if (isSocketConnected()) {
      dispatch(setConnected(true));
    }

    return () => {
      socket?.off('connect', handleConnect);
      socket?.off('disconnect', handleDisconnect);
      removeEventListener('robots_online', handleRobotsOnline);
      removeEventListener('telemetry_update', handleTelemetry);
      removeEventListener('command_ack', handleCommandAck);
      removeEventListener('robot_status', handleRobotStatus);
    };
  }, [isAuthenticated, dispatch]);

  // Notify backend about user login for privacy mode — fires ONCE globally
  useEffect(() => {
    if (isConnected && user && robots.length > 0 && !globalLoginNotified) {
      const robotSerials = robots.map(r => r.serial_number);
      notifyUserLoggedIn(user.id, robotSerials);
      globalLoginNotified = true;
    }
  }, [isConnected, user, robots]);

  const subscribeToRobot = useCallback((serial: string) => {
    wsSubscribe(serial);
    dispatch(addSubscribedRobot(serial));
  }, [dispatch]);

  const unsubscribeFromRobot = useCallback((serial: string) => {
    wsUnsubscribe(serial);
    dispatch(removeSubscribedRobot(serial));
  }, [dispatch]);

  const sendTeleop = useCallback((serial: string, linear: number, angular: number) => {
    wsTeleop(serial, linear, angular);
  }, []);

  const sendNavGoal = useCallback((serial: string, x: number, y: number, theta: number) => {
    wsNavGoal(serial, x, y, theta);
  }, []);

  const cancelNavigation = useCallback((serial: string) => {
    wsCancel(serial);
  }, []);

  const emergencyStop = useCallback((serial: string) => {
    wsEmergencyStop(serial);
  }, []);

  const isRobotOnline = useCallback((serial: string) => robotsOnline.includes(serial), [robotsOnline]);
  const getRobotTelemetry = useCallback((serial: string) => telemetry[serial], [telemetry]);

  return {
    isConnected, robotsOnline, subscribeToRobot, unsubscribeFromRobot,
    sendTeleop, sendNavGoal, cancelNavigation, emergencyStop, isRobotOnline, getRobotTelemetry,
  };
};
